import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Home, Phone, ArrowRight, AlertCircle } from 'lucide-react';

/**
 * 404 NotFound page.
 *
 * On mount, sets the HTTP status meta hint used by static/prerender infrastructure
 * (e.g. Netlify/Vercel/Emergent prerenderer) to return HTTP 404 for this route.
 * This works in tandem with any hosting configuration that promotes routes with
 * <meta name="prerender-status-code" content="404"> to a real HTTP 404.
 */
const NotFound = () => {
  useEffect(() => {
    // Signal a 404 to prerender/hosting layers that support meta status hints
    let el = document.head.querySelector('meta[name="prerender-status-code"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'prerender-status-code');
      document.head.appendChild(el);
    }
    el.setAttribute('content', '404');
    return () => el && el.remove();
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="not-found-page">
      <Seo
        title="Page Not Found (404) | MoveraCare Mobility"
        description="The page you requested could not be found on the MoveraCare Mobility website."
        path="/404"
        noindex
      />
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#F9A825]/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-[#F9A825]" />
          </div>
          <p className="text-sm font-bold tracking-widest text-[#F9A825] mb-2">ERROR 404</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-4 leading-tight">
            Page Not Found
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            The page you&apos;re looking for doesn&apos;t exist or may have moved. Below are a few places that can help.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center mb-10">
            <Link to="/">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="not-found-home">
                <Home className="mr-2 w-4 h-4" />
                Return Home
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold">
                Our Services
              </Button>
            </Link>
            <Link to="/book">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold">
                Book a Ride
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold" data-testid="not-found-call">
                <Phone className="mr-2 w-4 h-4" />
                (832) 925-2128
              </Button>
            </a>
          </div>
          <div className="bg-white rounded-xl border-2 border-gray-100 p-6 text-left">
            <h2 className="text-lg font-semibold text-[#004C97] mb-3">Popular pages</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <li><Link to="/services/wheelchair-transportation" className="text-[#004C97] hover:text-[#F9A825]">Wheelchair Transportation</Link></li>
              <li><Link to="/services/dialysis-transportation" className="text-[#004C97] hover:text-[#F9A825]">Dialysis Transportation</Link></li>
              <li><Link to="/services/hospital-discharge" className="text-[#004C97] hover:text-[#F9A825]">Hospital Discharge Transportation</Link></li>
              <li><Link to="/healthcare-partners" className="text-[#004C97] hover:text-[#F9A825]">Healthcare Partners</Link></li>
              <li><Link to="/healthcare-partners/workers-compensation" className="text-[#004C97] hover:text-[#F9A825]">Workers&apos; Compensation Transportation</Link></li>
              <li><Link to="/procurement" className="text-[#004C97] hover:text-[#F9A825]">Procurement &amp; Credentialing</Link></li>
              <li><Link to="/houston-tx-medical-transportation" className="text-[#004C97] hover:text-[#F9A825]">Houston Medical Transportation</Link></li>
              <li><Link to="/contact" className="text-[#004C97] hover:text-[#F9A825]">Contact</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
